import React, { useEffect, FC } from 'react';
import { useRouteError, Link } from 'react-router-dom';
import Logger from '@blink/components/src/utils/logger';
import { ReactComponent as ArrowRight } from '@blink/assets/src/icons/largeArrowRight.svg';

import App from './App';

import { Button, Text } from '@/shared/ui';
import { PATHS } from '@/routes/paths';
import { Zero, Four, Five } from '@/widgets/ErrorNumbers';

type FallbackProps = {
    error: Error;
    resetErrorBoundary: () => void;
};

export const ErrorBoundaryFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    useEffect(() => {
        Logger.error(error.message);
    }, [error]);

    return (
        <div className='flex flex-col items-center justify-center h-screen'>
            <div className={'flex'}>
                <Five />
                <Zero className={'mx-10'} />
                <Zero />
            </div>
            <Text type={Text.types.Display} size={Text.sizes.MD} className='my-8 font-medium'>
                Something went wrong
            </Text>
            <p className='text-gray-600'>Please try again</p>
            <Button
                variant={Button.types.Primary}
                onClick={resetErrorBoundary}
                className='mt-4 px-4 py-2 bg-blue-500 text-white rounded-md'
            >
                Try again
            </Button>
        </div>
    );
};

export const DefaultFallback: FC = () => {
    const error = useRouteError();

    const message = (error as Error)?.message;

    useEffect(() => {
        if (message) {
            Logger.error(message, { error });
        }
    }, [error]);

    return (
        <App>
            <div className='flex flex-col items-center justify-center h-[90vh]'>
                <div className={'flex'}>
                    <Five />
                    <Zero className={'mx-10'} />
                    <Zero />
                </div>
                <Text
                    type={Text.types.Heading}
                    size={Text.sizes.XS}
                    className='my-8 rounded bg-background-grayTransparent px-4 py-1'
                >
                    Error Encountered
                </Text>
                <Text type={Text.types.Display} size={Text.sizes.MD} className='mb-8 font-medium'>
                    Something went wrong
                </Text>
                <Link to={PATHS.HOME}>
                    <Button variant={Button.types.Secondary}>Try Again</Button>
                </Link>
            </div>
        </App>
    );
};

export const PageNotFound: FC = () => {
    const error = useRouteError();

    const message = (error as Error)?.message;

    useEffect(() => {
        if (message) {
            Logger.error(message, { error });
        }
    }, [message]);

    return (
        <div className='flex flex-col items-center justify-center h-[80vh]'>
            <div className={'flex'}>
                <Four />
                <Zero className={'mx-10'} />
                <Four />
            </div>
            <Text
                type={Text.types.Heading}
                size={Text.sizes.XS}
                className='my-8 rounded bg-background-grayTransparent px-4 py-1'
            >
                Page not found
            </Text>
            <Text type={Text.types.Display} size={Text.sizes.MD} className='mb-8 font-medium'>
                This is not the page you are looking for
            </Text>
            <Link to={PATHS.HOME}>
                <Button variant={Button.types.Secondary}>
                    Return Home
                    <ArrowRight className={'ml-2'} />
                </Button>
            </Link>
        </div>
    );
};
